/**
 * Dial-code catalog for the phone country picker.
 */

/** Country row in the dial-code picker. */
export interface PhoneCountry {
  code: string;
  name: string;
  dial: string;
  flag: string;
}

/** Dial-code catalog for the country picker. */
export const PHONE_COUNTRIES: PhoneCountry[] = [
  { code: 'FR', name: 'France', dial: '+33', flag: '🇫🇷' },
  { code: 'DZ', name: 'Algérie', dial: '+213', flag: '🇩🇿' },
  { code: 'AD', name: 'Andorre', dial: '+376', flag: '🇦🇩' },
  { code: 'BE', name: 'Belgique', dial: '+32', flag: '🇧🇪' },
  { code: 'BJ', name: 'Bénin', dial: '+229', flag: '🇧🇯' },
  { code: 'BF', name: 'Burkina Faso', dial: '+226', flag: '🇧🇫' },
  { code: 'BI', name: 'Burundi', dial: '+257', flag: '🇧🇮' },
  { code: 'KH', name: 'Cambodge', dial: '+855', flag: '🇰🇭' },
  { code: 'CM', name: 'Cameroun', dial: '+237', flag: '🇨🇲' },
  { code: 'CA', name: 'Canada', dial: '+1', flag: '🇨🇦' },
  { code: 'CF', name: 'Centrafrique', dial: '+236', flag: '🇨🇫' },
  { code: 'KM', name: 'Comores', dial: '+269', flag: '🇰🇲' },
  { code: 'CG', name: 'Congo', dial: '+242', flag: '🇨🇬' },
  { code: 'CI', name: "Côte d'Ivoire", dial: '+225', flag: '🇨🇮' },
  { code: 'DJ', name: 'Djibouti', dial: '+253', flag: '🇩🇯' },
  { code: 'GA', name: 'Gabon', dial: '+241', flag: '🇬🇦' },
  { code: 'GP', name: 'Guadeloupe', dial: '+590', flag: '🇬🇵' },
  { code: 'GN', name: 'Guinée', dial: '+224', flag: '🇬🇳' },
  { code: 'GQ', name: 'Guinée équatoriale', dial: '+240', flag: '🇬🇶' },
  { code: 'GF', name: 'Guyane', dial: '+594', flag: '🇬🇫' },
  { code: 'HT', name: 'Haïti', dial: '+509', flag: '🇭🇹' },
  { code: 'LA', name: 'Laos', dial: '+856', flag: '🇱🇦' },
  { code: 'LB', name: 'Liban', dial: '+961', flag: '🇱🇧' },
  { code: 'LU', name: 'Luxembourg', dial: '+352', flag: '🇱🇺' },
  { code: 'MG', name: 'Madagascar', dial: '+261', flag: '🇲🇬' },
  { code: 'ML', name: 'Mali', dial: '+223', flag: '🇲🇱' },
  { code: 'MA', name: 'Maroc', dial: '+212', flag: '🇲🇦' },
  { code: 'MQ', name: 'Martinique', dial: '+596', flag: '🇲🇶' },
  { code: 'MU', name: 'Maurice', dial: '+230', flag: '🇲🇺' },
  { code: 'MR', name: 'Mauritanie', dial: '+222', flag: '🇲🇷' },
  { code: 'YT', name: 'Mayotte', dial: '+262', flag: '🇾🇹' },
  { code: 'MC', name: 'Monaco', dial: '+377', flag: '🇲🇨' },
  { code: 'NE', name: 'Niger', dial: '+227', flag: '🇳🇪' },
  { code: 'NC', name: 'Nouvelle-Calédonie', dial: '+687', flag: '🇳🇨' },
  { code: 'PF', name: 'Polynésie française', dial: '+689', flag: '🇵🇫' },
  { code: 'CD', name: 'RD Congo', dial: '+243', flag: '🇨🇩' },
  { code: 'RE', name: 'Réunion', dial: '+262', flag: '🇷🇪' },
  { code: 'RW', name: 'Rwanda', dial: '+250', flag: '🇷🇼' },
  { code: 'BL', name: 'Saint-Barthélemy', dial: '+590', flag: '🇧🇱' },
  { code: 'MF', name: 'Saint-Martin', dial: '+590', flag: '🇲🇫' },
  { code: 'PM', name: 'Saint-Pierre-et-Miquelon', dial: '+508', flag: '🇵🇲' },
  { code: 'SN', name: 'Sénégal', dial: '+221', flag: '🇸🇳' },
  { code: 'SC', name: 'Seychelles', dial: '+248', flag: '🇸🇨' },
  { code: 'CH', name: 'Suisse', dial: '+41', flag: '🇨🇭' },
  { code: 'TD', name: 'Tchad', dial: '+235', flag: '🇹🇩' },
  { code: 'TG', name: 'Togo', dial: '+228', flag: '🇹🇬' },
  { code: 'TN', name: 'Tunisie', dial: '+216', flag: '🇹🇳' },
  { code: 'VU', name: 'Vanuatu', dial: '+678', flag: '🇻🇺' },
  { code: 'VN', name: 'Vietnam', dial: '+84', flag: '🇻🇳' },
  { code: 'WF', name: 'Wallis-et-Futuna', dial: '+681', flag: '🇼🇫' },
];

/** Extra EN/FR country-name aliases → ISO code. */
const NAME_ALIASES: Record<string, string> = {
  algeria: 'DZ',
  andorra: 'AD',
  belgium: 'BE',
  benin: 'BJ',
  cambodia: 'KH',
  cameroon: 'CM',
  'central african republic': 'CF',
  'république centrafricaine': 'CF',
  comoros: 'KM',
  'ivory coast': 'CI',
  'equatorial guinea': 'GQ',
  guinea: 'GN',
  'french guiana': 'GF',
  haiti: 'HT',
  laos: 'LA',
  lebanon: 'LB',
  morocco: 'MA',
  mauritius: 'MU',
  mauritania: 'MR',
  'new caledonia': 'NC',
  'french polynesia': 'PF',
  congo: 'CG',
  'republic of the congo': 'CG',
  'république du congo': 'CG',
  'democratic republic of the congo': 'CD',
  'république démocratique du congo': 'CD',
  reunion: 'RE',
  'saint barthelemy': 'BL',
  'saint barthélemy': 'BL',
  'saint martin': 'MF',
  'saint pierre and miquelon': 'PM',
  senegal: 'SN',
  switzerland: 'CH',
  chad: 'TD',
  tunisia: 'TN',
  'wallis and futuna': 'WF',
};

/** Country display name / ISO / alias → ISO code. */
export const COUNTRY_NAME_TO_ISO: Record<string, string> = {
  ...Object.fromEntries(
    PHONE_COUNTRIES.flatMap((country) => [
      [country.name.toLowerCase(), country.code],
      [country.code.toLowerCase(), country.code],
    ]),
  ),
  ...NAME_ALIASES,
};

/** Dial digits without `+` → first matching ISO code. */
export const COUNTRY_CODE_MAP: Record<string, string> = PHONE_COUNTRIES.reduce<
  Record<string, string>
>((acc, country) => {
  const digits = country.dial.slice(1);
  acc[digits] ??= country.code;
  return acc;
}, {});
